const NOTIFICATION_CHANNEL_PREFIX = 'notif_';
const NOTIFICATION_READ_CHANNEL_POSTFIX = 'pnread';
const PUBLIC_CHANNEL_POSTFIX = 'public';
const REMIND_CHANNEL_POSTFIX = 'remind';
const APPOINTMENT_CHANNEL_SUFFIX = 'appt';
export const DELETE_MESSAGE_ACTION_TYPE = 'deleted';

export const ReminderType = Object.freeze({
  SESSION: 'SESSION',
  APPOINTMENT: 'APPOINTMENT'
});

export const ApptNotificationActionType = Object.freeze({
  INVITE: 'INVITE',
  ACCEPT: 'ACCEPT',
  UPDATE: 'UPDATE',
  CANCEL: 'CANCEL',
  DECLINE: 'DECLINE',
  PROPOSE: 'PROPOSE',
  CREATE: 'CREATE'
});

export const NotificationActionType = Object.freeze({
  // This notification has no action buttons and will be removed from the notifications list only
  // when the user clicks on it (Eg: Auto-accept appointment notification sent to attendee)
  VIEW_ONLY: 'VIEW_ONLY',
  // This notification will be removed from the notification list either when the user views it
  // once in the notifications list or clicks on it. (Eg: Attendee 1 has accepted Appt1.).
  READ_ONLY: 'READ_ONLY',
  // This notification will be removed from the notification list when the user clicks on the notification
  // (takes an action like accept/decline or view conflicts). (Eg: Attendee 1 has accepted Appt1.).
  ACTIONABLE: 'ACTIONABLE'
});

export const NotificationLinkedContentPageType = Object.freeze({
  EXHIBITORS: 'Exhibitors',
  SESSIONS: 'Sessions',
  SCHEDULE: 'Schedule',
  HOME: 'Home',
  CUSTOM_PAGES: 'CustomPages',
  DOCUMENT_PAGES: 'DocumentPages',
  EXTERNAL_PAGES: 'ExternalPages',
  SURVEYS: 'Surveys',
  COMMUNITY: 'Community',
  PROFILE_MENU: 'ProfileMenu',
  ATTENDEE_CONNECTIONS: 'AttendeeConnections',
  ATTENDEE_LIST: 'AttendeeList',
  ATTENDEE_MESSAGING: 'AttendeeMessaging',
  ATTENDEE_PROFILE: 'AttendeeProfile',
  DISCUSSIONS: 'Discussions',
  DISCUSSIONS_LIST: 'DiscussionsList',
  EXHIBITORS_LIST: 'ExhibitorsList',
  FLOOR_PLAN: 'FloorPlan',
  FLOOR_PLANS_LIST: 'FloorPlansList',
  GAME: 'Game',
  GROUP_MESSAGING: 'GroupMessaging',
  ON_DEMAND_CONTENT_LIBRARY: 'OnDemandContentLibrary',
  MESSAGES: 'Messages',
  QR_CODE: 'QrCode',
  SESSIONS_LIST: 'SessionsList',
  SPEAKERS: 'Speakers',
  SPEAKERS_LIST: 'SpeakersList',
  SPEED_NETWORKING: 'SpeedNetworking',
  SPONSORS_LIST: 'SponsorsList',
  TRAVEL_DETAILS: 'TravelDetails',
  UPCOMING_EVENTS: 'UpcomingEvents',
  YOUR_EVENTS: 'YourEvents'
});

export const pageKeySlugs = new Map([
  [NotificationLinkedContentPageType.HOME, ''],
  [NotificationLinkedContentPageType.SESSIONS_LIST, 'sessions'],
  [NotificationLinkedContentPageType.EXHIBITORS_LIST, 'exhibitors'],
  [NotificationLinkedContentPageType.SPONSORS_LIST, 'sponsors'],
  [NotificationLinkedContentPageType.ATTENDEE_LIST, 'attendees'],
  [NotificationLinkedContentPageType.GAME, 'game'],
  [NotificationLinkedContentPageType.UPCOMING_EVENTS, 'upcomingevents'],
  [NotificationLinkedContentPageType.SPEED_NETWORKING, 'speednetworking'],
  [NotificationLinkedContentPageType.DISCUSSIONS_LIST, 'discussions'],
  [NotificationLinkedContentPageType.ATTENDEE_PROFILE, 'profile-edit'],
  [NotificationLinkedContentPageType.ON_DEMAND_CONTENT_LIBRARY, 'ondemand'],
  [NotificationLinkedContentPageType.YOUR_EVENTS, 'your-events'],
  [NotificationLinkedContentPageType.ATTENDEE_CONNECTIONS, 'connections'],
  [NotificationLinkedContentPageType.SPEAKERS_LIST, 'speakers'],
  [NotificationLinkedContentPageType.FLOOR_PLANS_LIST, 'floorplans'],
  [NotificationLinkedContentPageType.COMMUNITY, 'community'],
  [NotificationLinkedContentPageType.MESSAGES, 'messages']
]);

export const isNotificationChannel = channel =>
  channel && channel.startsWith(NOTIFICATION_CHANNEL_PREFIX);

export const isPublicNotificationChannel = channel =>
  isNotificationChannel(channel) && channel.endsWith(PUBLIC_CHANNEL_POSTFIX);

export const isRemindNotificationChannel = channel =>
  isNotificationChannel(channel) && channel.endsWith(REMIND_CHANNEL_POSTFIX);

export const isApptNotificationChannel = channel =>
  isNotificationChannel(channel) &&
  channel.endsWith(APPOINTMENT_CHANNEL_SUFFIX);

export const resolvePubnubError = err => {
  const { status: { statusCode, category, operation, errorData } = {} } =
    err || {};
  const {
    message,
    response: { req: { url: pubnubUrl } = {}, text: errorText } = {}
  } = errorData || {};

  if (pubnubUrl) {
    return {
      message,
      statusCode,
      category,
      operation,
      pubnubUrl,
      errorText
    };
  }
  return {
    operation,
    category,
    errorData
  };
};

export const isAttendeeReadChannel = channel =>
  isNotificationChannel(channel) &&
  channel.endsWith(NOTIFICATION_READ_CHANNEL_POSTFIX);

export const isPersistentNotificationChannel = channel =>
  isNotificationChannel(channel) &&
  !channel.endsWith(NOTIFICATION_READ_CHANNEL_POSTFIX) &&
  !channel.endsWith(REMIND_CHANNEL_POSTFIX);

/**
 * Message actions are used for planner & appointments notifications
 * @param channel
 * @returns {*|boolean}
 */
export const isMessageActionsNeededForChannel = channel =>
  (isNotificationChannel(channel) || isApptNotificationChannel(channel)) &&
  !channel.endsWith(NOTIFICATION_READ_CHANNEL_POSTFIX) &&
  !channel.endsWith(REMIND_CHANNEL_POSTFIX);

const MILLI_SEC_MULTIPLIER = 1000;
const NANO_SEC_DIVISOR = 10000000;

/**
 * this is the method that converts pubNub time token into a date.
 * @param {String} epochToNanoSecond epoch time stamp to the nano second returned by pubNub
 */
export const getDateFromEpochTimeToken = epochToNanoSecond => {
  const epochInSeconds = Math.ceil(
    parseInt(epochToNanoSecond, 10) / NANO_SEC_DIVISOR
  );
  return new Date(epochInSeconds * MILLI_SEC_MULTIPLIER);
};

/**
 * method to format the announcementMessage, into an object that can be represented on the ui.
 * @param {*} notificationMessage notification message to be formatted
 */
export const formatNotification = notificationMessage => {
  const { entry, timetoken, channelName } = notificationMessage;
  const {
    info: { content, title, linkedContent, metadata = {} } = {},
    id,
    type
  } = entry;
  return {
    channelName,
    id,
    body: content,
    title,
    linkedContent,
    metadata,
    publishedAt: getDateFromEpochTimeToken(timetoken),
    type
  };
};

/**
 * method to format seamless transition reminders.
 * @param {*} notificationMessage notification message to be formated
 */
export const formatReminder = notificationMessage => {
  const { entry, timetoken, channelName } = notificationMessage;
  const { info: { linkedContent } = {}, id: messageId } = entry;

  if (linkedContent?.contentType !== 'LinkedPage') {
    return null;
  }
  let itemType;
  if (linkedContent?.content?.type === 'Sessions') {
    itemType = ReminderType.SESSION;
  } else if (linkedContent?.content?.type === 'Appointment') {
    itemType = ReminderType.APPOINTMENT;
  } else {
    return null;
  }
  if (!linkedContent?.content?.id) {
    return null;
  }
  return {
    channelName,
    messageId,
    itemType,
    itemId: linkedContent.content.id,
    publishedAt: getDateFromEpochTimeToken(timetoken)
  };
};

/**
 * method returns the unique notification ids from, messages on -pnread channel for the attendee
 * message has the following schema
 * {
 *    announcementIds: [String!]!
 *    eventId: String!
 *    environment: String!
 * }
 * @param {Array} messages array of messages from announcementIds need to be extracted.
 */
export const fetchNotificationIdsFromMessages = messages => {
  const notificationIds = messages.reduce((ids, { entry }) => {
    ids.push(...entry.announcementIds);
    return ids;
  }, []);
  return [...new Set(notificationIds)];
};

/**
 * returns the array of formatted notifications.
 * @param {Array} notificationMessages array of notification messages to be formatted
 */
export const formatNotifications = notificationMessages =>
  notificationMessages.map(formatNotification);

export const filterAttendeeReadStatusChannel = channels =>
  channels.filter(isAttendeeReadChannel)[0];

/**
 * resolve after the delay
 * @param timeout timeout in ms
 */
export const sleep = (timeout = 0) => {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  });
};
